import React from "react";
import type { RouteComponentProps } from "react-router-dom";
import { useSelector } from "react-redux";

import type { AcknowledgedPolicy } from "@/core/types";
import { AcknowledgedPolicyEnum } from "@/core/constants";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { createAcknowledgementPayload } from "@/utils/acknowledgementUtilsTyped";
import { patientRequests } from "@/api/(requests)/patient";
import { selectPatientProfile } from "@/redux/selectors/authSelectors";
import { trackPolicyAcknowledgement } from "@/utils/acknowledgementUtils";

import { InsurancePolicy } from "./components/InsurancePolicy";
import { UpdatedCancellationPolicy } from "./components/CancellationPolicy";

type Props = {
  onAcknowledgementComplete: (name: string, data: unknown) => void;
} & RouteComponentProps<{ policy_name: AcknowledgedPolicy }>;

export function PolicyAcknowledgement({ match, onAcknowledgementComplete }: Props) {
  const profile = useSelector(selectPatientProfile);
  const { username } = profile;
  const policyName = match?.params?.policy_name;

  if (!policyName || !profile) return null;

  const submitHandler = async () => {
    try {
      const acknowledgements = { ...profile.acknowledgements };

      acknowledgements[policyName] = createAcknowledgementPayload({ consentResponse: true });

      await patientRequests.update_profile({ data: { acknowledgements, username } });

      onAcknowledgementComplete(policyName, acknowledgements[policyName]);
      trackPolicyAcknowledgement(policyName);
    } catch (error) {
      captureErrorMessage(parseError({ error, fallback: "Policy acknowledgement error" }), {
        tags: {
          component: "PolicyAcknowledgement",
          errorType: "acknowledgement_submission",
          policyName,
          username,
        },
      });
    }
  };

  return <>{renderPolicy(policyName, submitHandler)}</>;
}

const CANCELLATION_POLICY_DATE = "9/24/2021";

function renderPolicy(policyName: AcknowledgedPolicy, submitHandler: () => Promise<void>) {
  switch (policyName) {
    case AcknowledgedPolicyEnum.CANCELLATION_POLICY_NAME:
      return (
        <UpdatedCancellationPolicy
          effectiveDate={CANCELLATION_POLICY_DATE}
          submitHandler={submitHandler}
        />
      );

    case AcknowledgedPolicyEnum.OUT_OF_NETWORK_INSURANCE_POLICY_NAME:
      return (
        <InsurancePolicy
          policyName={AcknowledgedPolicyEnum.OUT_OF_NETWORK_INSURANCE_POLICY_NAME}
          submitHandler={submitHandler}
        />
      );

    case AcknowledgedPolicyEnum.UNINSURED_POLICY_NAME:
      return (
        <InsurancePolicy
          policyName={AcknowledgedPolicyEnum.UNINSURED_POLICY_NAME}
          submitHandler={submitHandler}
        />
      );

    default:
      return null;
  }
}
